import React, { useContext, useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import classnames from 'classnames';
import { getServerRuntimeConfig } from '../lib/runtimeConfig';
import { canonicalUrl } from '../helpers/canonical-url';
import { usePageView } from '../components/Analytics/utilities';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import Hero from '../components/Heros/Hero';
import ValueProp from '../components/ValueProp/ValueProp';
import BuiltForConstruction from '../components/BuiltForConstruction/BuiltForConstruction';
import CaseStudies from '../components/Discover/CaseStudies';
import Explore from '../components/Explore/Explore';
import ProcoreVideo from '../components/Marketing/ProcoreVideo';
import { useStyles } from './index.styles';
import ExploreLatestProjects from '../components/ExploreLatestProjects/ExploreLatestProjects';
import {
  getLatestProjects,
  getSearchResults,
} from '../services/project-search';
import { getGeoInfo, type GeoInfo } from '../services/geo-info';
import DistanceUnit from '../data/location/distance-units';
import { type ProjectsSearchRequest } from '../data/projectsSearch';
import { getGeolocation } from '../helpers/geolocation';
import { prettyPrintCityStateCountry } from '../services/geo-info';
import { getCityStateCountry } from '../data/location/autocomplete';
import AppContext from '../components/AppContext/AppContext';

const serverRuntimeConfig = getServerRuntimeConfig();

export async function getServerSideProps({ locale, query }) {
  const country = ['en-CA', 'fr-CA'].indexOf(locale) === -1 ? 'US' : 'CA';
  let initialResults = [];
  let geoInfo: GeoInfo = null;

  // Check if user provided a location in the query parameter (set by client-side detection)
  const userLocation = query?.userLocation?.toString();

  try {
    // If user has location info, fetch projects near them
    if (userLocation) {
      let geoInfoAndPolygons;
      try {
        geoInfoAndPolygons = await getGeoInfo(userLocation);
        geoInfo = geoInfoAndPolygons?.geoInfo;
      } catch (err) {
        console.error('Error getting geoInfo:', err);
      }

      if (geoInfo?.center) {
        // Fetch projects near the user's location
        const request: ProjectsSearchRequest = {
          businessRegistryUrl: serverRuntimeConfig.businessRegistry,
          geoInfo,
          page: 1,
          pageSize: 3,
          unit: DistanceUnit.Mile,
          radius: 100,
          loggingContext: 'Home Page - Near User',
          sectors: [],
          trades: [],
          classifications: [],
          query: '',
          projectDetails: [],
          fundingType: [],
          sort: 'last_updated',
        };

        const searchResults = await getSearchResults(request, false);
        initialResults = searchResults?.results || [];
      }
    }

    // If no location or no results from location search, fall back to latest projects
    if (!initialResults.length || initialResults.length < 3) {
      const result = await getLatestProjects(
        'Explore Latest Projects',
        3 - initialResults?.length
      );
      if (initialResults.length) {
        initialResults = [
          ...initialResults,
          ...(Array.isArray(result) ? result : []),
        ];
      } else {
        initialResults = Array.isArray(result) ? result : [];
      }
    }
  } catch (error) {
    console.error('Error getting projects', error);
    // Fall back to latest projects on any error
    try {
      const result = await getLatestProjects('Explore Latest Projects', 3);
      initialResults = Array.isArray(result) ? result : [];
    } catch (fallbackError) {
      console.error('Error getting latest projects fallback', fallbackError);
      initialResults = [];
    }
  }

  return {
    props: { country, initialResults },
  };
}

export default function Home({
  country,
  initialResults,
}: {
  country: string;
  initialResults: any;
}) {
  const router = useRouter();
  const classes = useStyles();
  const canonicalURL = canonicalUrl(router.asPath);
  const [projects, setProjects] = useState(initialResults);
  const [locationDetected, setLocationDetected] = useState(false);
  const { isUserLoggedIn } = useContext(AppContext);

  usePageView('Home');

  useEffect(() => {
    // Only detect location if we don't already have it in the URL
    if (!router.query.userLocation && !locationDetected && isUserLoggedIn) {
      detectAndFetchNearbyProjects();
    }
  }, []);

  const detectAndFetchNearbyProjects = async () => {
    try {
      // Get user's current location from browser (client-side only)
      const currentGeolocation = await getGeolocation();

      if (!currentGeolocation) {
        return;
      }

      const locationData = getCityStateCountry(currentGeolocation);
      const locationString = prettyPrintCityStateCountry(locationData, true);

      if (!locationString) {
        return;
      }

      setLocationDetected(true);

      // Refresh the page with location to trigger SSR with nearby projects
      router.replace(
        `/?userLocation=${encodeURIComponent(locationString)}`,
        undefined,
        { shallow: false }
      );
    } catch (error) {
      console.error('Error detecting location:', error);
    }
  };

  // Update projects when initialResults change (after page refresh with location)
  useEffect(() => {
    /* istanbul ignore next */
    setProjects(initialResults);
  }, [initialResults]);

  return (
    <>
      <Head>
        <link rel="canonical" key="canonical" href={canonicalURL} />
      </Head>
      <Header hero />
      <section role="main">
        <Hero />
        <Box aria-label="Explore">
          <Explore country={country} />
        </Box>
        <Box
          className={classnames(
            classes.browseContainer,
            classes.procoreVideoContainer
          )}
        >
          <ProcoreVideo />
        </Box>
        {country === 'US' && (
          <Box className={classes.browseContainer}>
            <ExploreLatestProjects projects={projects} />
          </Box>
        )}
        <Box className={classes.browseContainer}>
          <ValueProp country={country} />
        </Box>

        <Box className={classes.browseContainer}>
          <CaseStudies />
        </Box>
        <Box className={classes.browseContainer}>
          <BuiltForConstruction />
        </Box>
      </section>
      <Footer />
    </>
  );
}
